import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  NormalAutoCompleteSearchSelectionField,
  NormalFormCountrySelectionField,
  NormalFormTextField,
} from '../molecules';
import {
  useGetAllCitiesLazyQuery,
  useGetAllStatesLazyQuery
} from '@/graphql/generated';
import textsData from '@/textData/AddressFormTexts.json';
import { useUserLanguage } from '@/customHooks';
import { AddressProps } from '@/custom-types';

const emptyData: AddressProps = {
  street: '',
  buildingNumber: '',
  addressLine: '',
  city: '',
  state: '',
  country: '',
  zipCode: '',
};

type AddressFormFieldsProps = {
  onChange: (address: AddressProps) => void;
  readOnly?: boolean;
  defaultAddress?: AddressProps;
};

export const AddressFormControlled = memo(({ onChange, readOnly, defaultAddress }: AddressFormFieldsProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const [getStates, { data: statesData }] = useGetAllStatesLazyQuery();
  const [getCities, { data: citiesData }] = useGetAllCitiesLazyQuery();

  const stateOptions = useMemo(() => 
    statesData?.states.map((state) => ({ 
      name: state.name, 
      value: state.geonamesAdmin1Code 
    })) ?? [],
    [statesData]
  );

  const cityOptions = useMemo(() =>
    citiesData?.cities.map((city) => ({
      name: city.name,
      value: city.geonamesId
    })) ?? [],
    [citiesData]
  );

  const [formAddress, setFormAddress] = useState<AddressProps>(defaultAddress || emptyData);

  useEffect(() => {
    setFormAddress(defaultAddress || emptyData);
  }, [defaultAddress]);

  useEffect(() => {
    if (defaultAddress?.country) {
      getStates({ variables: { countryCode: defaultAddress.country } });
    }
    if (defaultAddress?.country && defaultAddress?.state) {
      getCities({ variables: { stateCode: defaultAddress.state } });
    }
  }, [defaultAddress, getStates, getCities]);

  const handleCountryChange = useCallback((value: string | null) => {
    const nextAddress = { ...formAddress, country: value || '', state: '', city: '' };
    setFormAddress(nextAddress);
    onChange(nextAddress);
    if (value) getStates({ variables: { countryCode: value } });
  }, [formAddress, getStates, onChange]);

  const handleStateChange = useCallback((value: string | null) => {
    const nextAddress = { ...formAddress, state: value || '', city: '' };
    setFormAddress(nextAddress);
    onChange(nextAddress);
    if (value) getCities({ variables: { stateCode: value } });
  }, [formAddress, getCities, onChange]);

  const handleCityChange = useCallback((value: string | null) => {
    const nextAddress = { ...formAddress, city: value || '' };
    setFormAddress(nextAddress);
    onChange(nextAddress);
  }, [formAddress, onChange]);

  const handleFieldChange = useCallback((field: keyof AddressProps, value: string | null) => {
    const nextAddress = { ...formAddress, [field]: value || '' };
    setFormAddress(nextAddress);
    onChange(nextAddress);
  }, [formAddress, onChange]);
  
  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="md:col-span-5">
          <NormalFormTextField
            label={texts.street}
            placeholder={texts.streetPlaceholder}
            name="addressStreet"
            readOnly={readOnly}
            value={formAddress.street}
            onChange={(value) => handleFieldChange('street', value)}
          />
        </div>
        <div className="md:col-span-5">
          <NormalFormTextField
            label={texts.buildingNumber}
            placeholder={texts.buildingNumberPlaceholder}
            name="addressBuildingNumber"
            readOnly={readOnly}
            value={formAddress.buildingNumber}
            onChange={(value) => handleFieldChange('buildingNumber', value)}
          />
        </div>
        <div className="md:col-span-2">
          <NormalFormTextField
            label={texts.zipCode}
            placeholder={texts.zipCodePlaceholder}
            name="addressZipCode"
            readOnly={readOnly}
            value={formAddress.zipCode}
            onChange={(value) => handleFieldChange('zipCode', value)}
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="md:col-span-4">
          <NormalFormCountrySelectionField
            label={texts.country}
            noValueText={texts.countryNoValueText}
            value={formAddress.country}
            name="addressCountry"
            readOnly={readOnly}
            onChange={handleCountryChange}
          />
        </div>
        <div className="md:col-span-4">
          <NormalAutoCompleteSearchSelectionField
            label={texts.state}
            name="addressState"
            readOnly={readOnly}
            value={formAddress.state}
            onChange={handleStateChange}
            options={stateOptions}
          />
        </div>
        <div className="md:col-span-4">
          <NormalAutoCompleteSearchSelectionField 
            label={texts.city}
            name="addressCity"
            readOnly={readOnly}
            value={formAddress.city}
            onChange={handleCityChange}
            options={cityOptions} // Usamos las opciones memoizadas
          />
        </div>
      </div>
      <div className="mt-4">
          <NormalFormTextField
            label={texts.addressLine}
            placeholder={texts.addressLinePlaceholder}
            name="addressAddressLine"
            readOnly={readOnly}
            value={formAddress.addressLine}
            onChange={(value) => handleFieldChange('addressLine', value)}
          />
      </div>
    </>
  );
});
